import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Pencil, Trash2, Building2, X } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PAGE_SIZE = 50;

function planBadge(plan) {
  const map = {
    TRIAL: "bg-slate-100 text-slate-700",
    BASIC: "bg-blue-50 text-blue-700",
    PRO: "bg-teal-50 text-teal-700",
    ENTERPRISE: "bg-purple-50 text-purple-700",
  };
  return map[plan] || "bg-slate-100 text-slate-700";
}

function statusBadge(status) {
  const map = {
    ACTIVE: "bg-green-50 text-green-700",
    EXPIRED: "bg-amber-50 text-amber-700",
  };
  return map[status] || "bg-slate-100 text-slate-700";
}

function UsageBar({ used, limit }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const barColor = pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-teal-600";
  return (
    <div className="w-28">
      <div className="flex justify-between text-xs text-slate-600">
        <span>{used}</span>
        <span className="text-slate-400">/ {limit}</span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full ${barColor} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function initials(name) {
  if (!name) return "";
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

const FILTER_LABELS = {
  plan: (v) => `Plan: ${v}`,
  status: (v) => `Status: ${v}`,
  expired: () => "Expired Subscriptions",
  paid: () => "Paid Clinics",
};

export default function ClinicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState({ clinics: [], total: 0 });
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const activeFilters = {
    plan: searchParams.get("plan") || "",
    status: searchParams.get("status") || "",
    expired: searchParams.get("expired") === "true",
    paid: searchParams.get("paid") === "true",
  };

  const load = () => {
    const params = { page, page_size: PAGE_SIZE, search };
    if (activeFilters.plan) params.plan = activeFilters.plan;
    if (activeFilters.status) params.status = activeFilters.status;
    if (activeFilters.expired) params.expired = true;
    if (activeFilters.paid) params.paid = true;
    api.get("/admin/clinics", { params }).then(({ data }) => setResult(data));
  };

  useEffect(() => {
    const timeout = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchParams]);

  const clearFilters = () => setSearchParams({});

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/clinics/${deleteTarget.id}`);
      toast.success(`${deleteTarget.clinic_name} and all its records were permanently deleted.`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
  const activeFilterChips = Object.entries(activeFilters).filter(([, v]) => v);

  return (
    <AdminLayout>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-slate-900" data-testid="clinics-page-heading">
          Clinics
        </h1>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            data-testid="clinics-search-input"
            placeholder="Search by clinic, code, doctor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="mt-4 flex items-center gap-2" data-testid="active-filter-chips">
          {activeFilterChips.map(([key, value]) => (
            <span key={key} className="rounded-full bg-teal-50 px-3 py-1 text-xs font-medium text-teal-700">
              {FILTER_LABELS[key](value)}
            </span>
          ))}
          <button data-testid="clear-filters-btn" onClick={clearFilters} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">
            <X className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      <div className="mt-6">
        <div data-testid="clinics-table-scroll-container">
          <Table className="min-w-[1680px]">
            <TableHeader>
              <TableRow className="sticky top-0">
                <TableHead className="py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Clinic</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Code</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Doctor</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Specialization</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Appointments</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Staff</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Start Date</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">Expiry</TableHead>
                <TableHead className="sticky right-0 z-20 bg-slate-50 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 shadow-[-6px_0_12px_-4px_rgba(15,23,42,0.10)]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {result.clinics.map((c, i) => (
                <TableRow
                  key={c.id}
                  className="group transition-colors hover:bg-teal-50/40"
                  data-testid={`clinic-row-${c.clinic_code}`}
                >
                  <TableCell className="py-3 whitespace-nowrap">
                    <Link to={`/ali-admin-portal/clinics/${c.id}`} className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-xs font-semibold text-teal-700">
                        {initials(c.clinic_name) || <Building2 className="h-4 w-4" />}
                      </span>
                      <span className="font-medium text-slate-900 group-hover:text-teal-700 group-hover:underline">
                        {c.clinic_name}
                      </span>
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs text-slate-500">{c.clinic_code}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{c.doctor_name}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{c.specialization || "-"}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{c.mobile || "-"}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-700">{c.email || "-"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${planBadge(c.subscription_plan)}`}>
                      {c.subscription_plan}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadge(c.status)}`}>
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <UsageBar used={c.appointments_used} limit={c.appointments_limit} />
                  </TableCell>
                  <TableCell className={`whitespace-nowrap ${c.staff_used > c.max_staff ? "font-medium text-red-600" : "text-slate-700"}`}>
                    {c.staff_used} / {c.max_staff}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-slate-600">{formatDate(c.subscription_start)}</TableCell>
                  <TableCell className="whitespace-nowrap text-slate-600">{formatDate(c.subscription_end)}</TableCell>
                  <TableCell
                    className="sticky right-0 z-10 whitespace-nowrap bg-white text-right shadow-[-6px_0_12px_-4px_rgba(15,23,42,0.10)] transition-colors group-hover:!bg-teal-50"
                  >
                    <div className="flex justify-end gap-1.5">
                      <button
                        data-testid={`edit-clinic-${c.clinic_code}`}
                        title="Edit Clinic"
                        onClick={() => navigate(`/ali-admin-portal/clinics/${c.id}`)}
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-teal-700 hover:bg-teal-50 hover:text-teal-700"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`delete-clinic-${c.clinic_code}`}
                        title="Delete Clinic"
                        onClick={() => setDeleteTarget(c)}
                        className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:border-red-600 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {result.clinics.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} className="py-10 text-center text-slate-400">
                    No clinics found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600" data-testid="clinics-pagination">
        <span>
          Page {page} of {totalPages} ({result.total} clinics)
        </span>
        <div className="flex gap-2">
          <Button data-testid="clinics-pagination-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Button data-testid="clinics-pagination-next" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-clinic-modal">
          <DialogHeader>
            <DialogTitle>Permanently delete {deleteTarget?.clinic_name}?</DialogTitle>
            <DialogDescription>
              This action is <span className="font-semibold text-red-600">irreversible</span>. The clinic, its Doctor
              and Receptionist accounts, and ALL associated patients, appointments, prescriptions, and reports will
              be permanently removed from the database. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" data-testid="delete-clinic-cancel-btn" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              data-testid="delete-clinic-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
              onClick={confirmDelete}
            >
              {deleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
